import React, { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Steps from './steps/index';
import ResultVersionControl from "./ResultVersionControl";
import { HeaderProgressTracker } from '../Common/HeaderStepper/HeaderProgressTracker';
import {
  setBasicInfo,
  getSessionStorageItem,
  SESSION_STORAGE_CUSTOMIZED_TERM_KEY,
  SESSION_STORAGE_ACCOUNT_KEY,
  getBasicInfo,
  deleteConsumerApplicationId,
  SESSION_STORAGE_BASIC_INFO_KEY, SESSION_STORAGE_TERM_SORT_FILTER_KEY,
  SESSION_STORAGE_CUSTOMIZED_HEALTH_KEY, SESSION_STORAGE_CUSTOMIZED_TEASER_KEY, SESSION_STORAGE_UTM_DATA,
  SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY
} from '../../../sessionStorage';
import TermData from '../../Helpers/Data/Products/Term/TermData';
import { saveSessionEvent } from "../../../session";
import { format } from 'date-fns';
import TobaccoUsage from "../Common/Helpers/TobaccoUsage";
import { BestFeatures, EasySteps } from "./Helpers/Constant";
import { GetHandoffPath } from "../Common/GetHandoffPath";
import GetProductDetail from '../../Common/GetProductDetail';
import { JourneyTypes } from "../../Helpers/Constants/JourneyTypes";
import { getActiveQuestionList, PRODUCT_URLS } from "../../Helpers/Constants/Constant";
import { QuoteProductID } from "../Common/QuoteProductID";
import '../Quoter.scss';
import './CustomizeTerm.scss';

export default function CustomizeTerm() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { saveSessionEvent() }, [location])
  const isSimplifiedIssueTerm = location.state && location.state.isFromSimplifiedIssueTerm ? true : false;
  const termNavigationState = isSimplifiedIssueTerm ? { state: { isFromSimplifiedIssueTerm: true } } : {};

  const path = location ? location.pathname.split("/").pop() : null;
  const account = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const isPreQuoteEnabled = account?.account?.enableTermPreQuote;

  if (path === 'ns') {
    //clear product session
    sessionStorage.removeItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_CUSTOMIZED_HEALTH_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_TERM_SORT_FILTER_KEY);
  }

  // 27514 - Since E*Trade is unable to change the URL on their side, redirect to their landing page when they hit /term-insurance
  useEffect(() => {
    const productDetail = GetProductDetail(isSimplifiedIssueTerm ? ['/landing/simplified-issue-term'] : ['/landing/term-insurance', '/term-insurance']);
    if (productDetail === undefined) {
      navigate('/');
    }
    if (location.pathname === '/term-insurance' && account.account.accountName.toLowerCase().indexOf("etrade") !== -1) {
      navigate('/landing/etrade-term');
    }
  }, []);

  const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY);
  const sessionStorageTermSettings = getSessionStorageItem(SESSION_STORAGE_TERM_SORT_FILTER_KEY);
  const teaserRateObject = getSessionStorageItem(SESSION_STORAGE_CUSTOMIZED_TEASER_KEY) || {};
  const activeQuestionList = getActiveQuestionList(isSimplifiedIssueTerm ? '/landing/simplified-issue-term' : PRODUCT_URLS.TERM);
  const prefill = location.search && location.search.toLowerCase().includes('prefill');
  const basicInfo = getBasicInfo();
  const teaserInfo = {
    faceAmount: (basicInfo && basicInfo.faceAmount) || (teaserRateObject.params && teaserRateObject.params.faceAmount),
    termLength: teaserRateObject.params && teaserRateObject.params.termLength,
    healthClass: teaserRateObject.params && teaserRateObject.params.healthClass,
  };

  const newFormState = {
    currentStep: 1,
    pages: {
      preQuote: Steps.PreQuote.DefaultFormState(prefill, teaserInfo, basicInfo),
      quoter: Steps.Quoter.DefaultFormState(basicInfo),
      selectAppointment: Steps.SelectAppointment.DefaultFormState(prefill, basicInfo, activeQuestionList),
    },
  };

  const initialFormState = sessionStorageFormState || newFormState;

  if (sessionStorageTermSettings) {
    initialFormState.pages.quoter.coverageAmount = sessionStorageTermSettings.term.coverageAmount;
    initialFormState.pages.quoter.coverageLength = sessionStorageTermSettings.term.termLength;
    initialFormState.pages.quoter.healthClass = sessionStorageTermSettings.term.healthClass;
  }
  
  const [formState, setFormState] = useState(initialFormState);
  const termData = TermData();
  const [multiTermResult, setMultiTermResult] = useState(false);
  const journeyType = formState.pages.quoter.isSelectOrTalk === "contactSpecialist" ? JourneyTypes.CONTACT_A_SPECIALIST : JourneyTypes.APP_REQUEST;
  const productDetail = GetProductDetail(isSimplifiedIssueTerm ? ['/landing/simplified-issue-term'] : ['/landing/term-insurance', '/term-insurance']);
  const resultScreenVersion = productDetail.resultScreenVersion;
  const quoteResultSortDefault = productDetail.quoteResultSortDefault;
  
  // When coming from the landing page form, skip to step 2
  useEffect(() => {
    if (isPreQuoteEnabled && location.search.includes('landing') && formState.currentStep === 1) {
      setFormState({ ...formState, currentStep: 2 });
    }
    if (!isPreQuoteEnabled && path === 'prequote') {
      navigate('/term-insurance/quote-results', { replace: true, ...termNavigationState });
    }
  }, [isPreQuoteEnabled, path]);

  // update the formState when the url path changes the step
  useEffect(() => {
    if (!path) {
      return;
    }

    const nextStep = steps.find(s => s.path === path);

    if (!nextStep) {
      return;
    }

    // prevent submitted app alterations
    if (!sessionStorageFormState && formState.currentStep === steps.length) {

      // if they are backing or directly going to another step, send them to product root
      if (formState.currentStep !== nextStep.step) {
        navigate('/term-insurance', termNavigationState);
      }
    }
    const newFullFormState = { ...formState, currentStep: nextStep.step };
    setFormState(newFullFormState);
  }, [path]);

  const setEmailPrefill = (basicInfo) => {
    setBasicInfo(basicInfo);
  };
  
  const setQuoteEmail = () => {
    const basicInfo = getBasicInfo();
    onFieldChange("selectAppointment", "email", basicInfo.email);
  };

  const onNext = () => {
    const nextStep = steps.find(s => s.step === formState.currentStep + 1);

    if (!nextStep) {
      throw new Error("onNext did not find a next step");
    }

    const newFullFormState = { ...formState, currentStep: nextStep.step };

    setFormState(newFullFormState);
    sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(newFullFormState));

    navigate(`/term-insurance/${nextStep.path}`, termNavigationState);
    window.scroll(0, 0);
  };

  const onBack = () => {
    let backStep = steps.find(s => s.step === formState.currentStep - 1);
    if (isPreQuoteEnabled && formState.currentStep === 3) {
      backStep = steps.find(s => s.path === 'prequote'); // Select appointment should go back to prequote, if enabled
    }

    if (!backStep) {
       navigate(`/landing/term-insurance`, termNavigationState);
       return;
    }  

    const newFullFormState = { ...formState, currentStep: backStep.step };

    setFormState(newFullFormState);
    sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(newFullFormState));
    navigate(`/term-insurance/${backStep.path}`, termNavigationState);
    window.scroll(0, 0);
  };

  const onFieldChange = (step, field, value) => {
    setFormState({ ...formState, pages: { ...formState.pages, [step]: { ...formState.pages[step], [field]: value } } });
  };

  const logFinalDigitalQuote = (email, firstName, lastName, quoter, oneClickTypeId, referralId = null, applicationId = null) => {
    const isSelectedQuoteCarrier = quoter.selectedQuote.carrierName !== "" && quoter.selectedQuote.carrierName !== null;
    const utmData = getSessionStorageItem(SESSION_STORAGE_UTM_DATA);

    return new Promise(resolve => {
      //save quote log for bms
      //keep in mind that this needs to match 1click quote log format
      const requestString = JSON.stringify({
        CoverageAmount: quoter.coverageAmount,
        CoverageLength: quoter.term,
        State: basicInfo.state,
        BirthDate: format(new Date(basicInfo.birthdate), 'yyyy-MM-dd'),
        HealthClass: quoter.healthClass,
        Gender: basicInfo.gender,
        Tobacco: TobaccoUsage(basicInfo.tobacco),
        Email: email,
        FirstName: firstName,
        LastName: lastName,
        selectedProductID: isSelectedQuoteCarrier && quoter.selectedQuote.carrierName.toLowerCase().includes("lga") ? quoter.selectedQuote.productID : "",
      });

      let jsonResponse;

      // set custom quote id in response if present
      if (referralId) {
        const response = { ...quoter.selectedQuote };
        response.PartnerQuoteId = referralId;
        response.UTMs = utmData || "";

        jsonResponse = JSON.stringify(response);
      } else {
        quoter.selectedQuote.UTMs = utmData || "";
        jsonResponse = JSON.stringify(quoter.selectedQuote);
      }
      const pathName = GetHandoffPath(quoter.selectedQuote.productID);
      saveSessionEvent(true, pathName);
      //clear product session

      const accessToken = getSessionStorageItem(SESSION_STORAGE_SSO_ACCESS_TOKEN_KEY);
      const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

      fetch(`${covrServicesUrl}/api/QuoteLog/ArchiveQuoteLog`, {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
          'SessionId': account.sessionId,
          'X-Consumer-Application-ID': account.consumerApplicationId,
          'Authorization': accessToken ? 'Bearer ' + accessToken : null,
        },
        body: JSON.stringify({
          QuoteLogId: quoter.selectedQuote.quoteLogId,
          OneClickTypeID: oneClickTypeId || 52, //digital oneclick type
          Request: requestString,
          Response: jsonResponse,
          ApplicationID: applicationId || account.sessionId,
          OneClickAccountID: account.account.oneClickAccountId,
          SessionID: account.sessionId,
          ConsumerAccountId: account.account.accountId,
          PartnerQuoteId: referralId,
        }),
      })
      .then(deleteConsumerApplicationId)
      .then(() => {
        sessionStorage.removeItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_BASIC_INFO_KEY);
        resolve();
      });
    });
  };

  const onNextHandOff = (productId) => {
    if(productId === QuoteProductID.PRUDENTIAL_SIMPLY_TERM_PRODUCT_ID){
      navigate(`/term-insurance/prudential`);
    }
    else{
      navigate(`/term-insurance/lga`);
    }
    window.scroll(0, 0);
  };

  const goBack = () => {
    navigate(-1); 
  };

  const calculatePercentage = (totalStep, step, categoryStep, currentCateTotalStep) => {
    const stepPer = ((step - 1) / totalStep * 100);
    const cateCount = currentCateTotalStep === 1 ? 1 : (((categoryStep - 0.5) / currentCateTotalStep) * (100 / totalStep));
    return stepPer + cateCount;
  };

  const steps = [
    ...(isPreQuoteEnabled ? [{
      step: 1,
      path: 'prequote',
      progressTitle: 'Quick Quote',
      title: "TERM LIFE INSURANCE  /  CUSTOMIZE YOUR COVERAGE",
      component: (
        <Steps.PreQuote
          formState={formState.pages.preQuote}
          teaserRate={teaserRateObject.rate || undefined}
          fullFormState={formState}
          basicInfo={basicInfo}
          quoter={formState.pages.quoter}
          term30EnableTeaserRate={account.term30EnableTeaserRate}
          onFieldChange={(field, value) => onFieldChange('preQuote', field, value)}
          setFormState={(coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                quoter: {
                  ...formState.pages.quoter,
                  coverageAmount: coverageAmount,
                  term: termLength,
                  healthClass: healthClass
                }
              },
            })
          }
          onMultiFieldChange={(fs) => {
            setFormState(fs);
            sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(fs));
          }}
          onNext={onNext}
          onBack={onBack}
          onMultiTerm={(multi) => setMultiTermResult(multi)}
          coverageAmountSpecs={termData.specs.coverageAmount}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    }] : []),
    {
      step: 2,
      path: 'quote-results',
      progressTitle: 'Getting Started',
      component: <ResultVersionControl 
        result={{
          version: resultScreenVersion,
          quoteResultSortDefault: quoteResultSortDefault,
          productType: "Term3",
          formStatePagesQuoter: formState.pages.quoter,
          fullFormState: formState,
          preQuoteDetails: formState.pages.preQuote,
          isPreQuoteEnabled: isPreQuoteEnabled,
          basicInfo: basicInfo,
          onNextHandOff: onNextHandOff,
          onNext: onNext,
          onBack: onBack,
          onFieldChange: (field, value) => onFieldChange('quoter', field, value),
          setFormState: (coverageAmount, termLength, healthClass) =>
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                quoter: {
                  ...formState.pages.quoter,
                  coverageAmount: coverageAmount,
                  term: termLength,
                  healthClass: healthClass
                }
              },
            }),
          onMultiFieldChange: (fs) => {
            setFormState(fs);
            sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(fs));
          },
          setEmailPrefill: setEmailPrefill,
          setQuoteEmail: setQuoteEmail,
          coverageAmountSpecs: termData.specs.coverageAmount,
          multiTermResult: multiTermResult,
        }}
      />,
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 3,
      path: 'select-appointment',
      progressTitle: 'Schedule your application',
      title: "TERM LIFE INSURANCE  /  SCHEDULE CALL",
      component: (
        <Steps.SelectAppointment
          activeQuestionList={activeQuestionList}
          formState={formState.pages.selectAppointment}
          quoter={formState.pages.quoter}
          data={BestFeatures}
          journeyType={journeyType}
          title="Your Plan's Best Features"
          basicInfo={basicInfo}
          onFieldChange={(field, value) => onFieldChange('selectAppointment', field, value)}
          onNext={onNext}
          setMultiField={(firstName, lastName, email) => {
            setFormState({
              ...formState,
              pages: {
                ...formState.pages,
                selectAppointment: { ...formState.pages.selectAppointment, firstName: firstName, lastName: lastName, email: email }
              }
            });
            sessionStorage.setItem(SESSION_STORAGE_CUSTOMIZED_TERM_KEY, JSON.stringify(formState));
          }}
          fullFormState={formState}
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 4,
      path: 'thank-you',
      progressTitle: 'Schedule your application',
      title: "TERM LIFE INSURANCE  /  CALL SCHEDULED",
      component: (
        <Steps.ScheduleAppointment
          quoter={formState.pages.quoter}
          appointment={formState.pages.selectAppointment}
          data={EasySteps}
          title='What happens next'
        />
      ),
      hideProgressTracker: true,
      showContainer: false,
      fullWidth: true,
    },
    {
      step: 5,
      path: 'lga',
      title: 'Your Life Insurance Request',
      progressTitle: 'Getting Started',
      component: <Steps.Lga
        formData={formState.pages}
        basicInfo={basicInfo}
        showCustomizeTermModal={true}
        onBack={goBack}
        logQuote={logFinalDigitalQuote}
      />,
      hideProgressTracker: true,
      hidden: false,
    },
    {
      step: 6,
      path: 'prudential',
      title: 'Your Life Insurance Request',
      progressTitle: 'Getting Started',
      component: <Steps.Prudential
        formData={formState.pages}
        basicInfo={basicInfo}
        showCustomizeTermModal={true}
        onBack={goBack}
        logQuote={logFinalDigitalQuote}
      />,
      hideProgressTracker: true,
      hidden: false,
    },
  ];

  const currentStepObj = steps.find(s => s.path === path) || steps.find(s => s.step === formState.currentStep) || steps[0];
  const categories = steps.filter((o) => o.categoryNumber === currentStepObj.categoryNumber);
  const totalCategoriesStep = Math.max.apply(Math, categories.map((o) => {
    return o.categoryStep;
  }));
  const percent = calculatePercentage(3, currentStepObj.categoryNumber, currentStepObj.categoryStep, totalCategoriesStep);

  return (
    <div>
      <Helmet>
        <title>{`Term life insurance${currentStepObj?.progressTitle ? ` - ${currentStepObj.progressTitle}` : ''}`}</title>
      </Helmet>

      <HeaderProgressTracker showCustomizeProgressTracker={true} title={currentStepObj.title}
                            hideMainTitle={!currentStepObj.title}
                             hidden={currentStepObj.hidden} customClassNames={"customize-term"}
                             showContainer={currentStepObj.showContainer}
                             hideProgressTracker={currentStepObj.hideProgressTracker} headerProductsText={true}
                             stepName={currentStepObj.progressTitle} step={currentStepObj.categoryNumber} totalSteps={3}
                             whiteBg={currentStepObj.whiteBackground} percent={percent}
                             fullWidth={currentStepObj.fullWidth}>
        <div className='CustomizeTerm pb-3'>
          {currentStepObj.component}
        </div>
      </HeaderProgressTracker>
    </div>
  );
}
